import { useMemo, useState } from "react";
import { collections } from "@/mocks/guatrend";

interface Props {
  category: string;
  keyword: string;
}

const presentCats = ["全部", ...Array.from(new Set(collections.map((c) => c.category)))];

export default function CollectionSection({ category, keyword }: Props) {
  const [tab, setTab] = useState(category || "全部");
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const activeTab = category || tab;

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return collections.filter((c) => {
      const matchCat = activeTab === "全部" || c.category === activeTab;
      const matchKw =
        !kw ||
        c.title.toLowerCase().includes(kw) ||
        c.category.includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeTab, keyword]);

  const toggleSaved = (id: string) => {
    setSaved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section id="collection" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-medium text-primary-600 whitespace-nowrap">CURATED COLLECTION</p>
            <h2 className="font-display mt-2 text-2xl text-foreground-900 md:text-4xl">高分精选合集，网友都在看的优质好瓜</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-foreground-600">
            每一份合集都经过真实网友点评与评分筛选，评分越高，越值得一看。
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {presentCats.map((ct) => (
            <button
              key={ct}
              type="button"
              onClick={() => setTab(ct)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeTab === ct
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:text-primary-600"
              }`}
            >
              {ct}
            </button>
          ))}
          {keyword && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-700">
              搜索：{keyword}
              <i className="ri-close-line"></i>
            </span>
          )}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">这个分类 / 关键词的高分合集正在甄选中</p>
            <p className="mt-1 text-sm text-foreground-500">先去看看今日口碑热度榜，或换个关键词试试吧。</p>
            <a
              href="#hot"
              className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              去看今日榜单
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((c) => {
              const isSaved = saved.has(c.id);
              return (
                <article
                  key={c.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:shadow-lift"
                >
                  <div className="relative h-44 w-full overflow-hidden md:h-48">
                    <img
                      src={c.image}
                      alt={`${c.title} 高分合集封面`}
                      className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 inline-flex items-center whitespace-nowrap rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-primary-700 backdrop-blur">
                      {c.category}
                    </span>
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-500/95 px-2.5 py-1 font-display text-sm text-background-50">
                      <i className="ri-star-fill text-xs"></i>
                      {c.score}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-900 md:text-lg">
                      {c.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{c.excerpt}</p>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {c.tags.map((t) => (
                        <span key={t} className="whitespace-nowrap rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                          #{t}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-4">
                      <span className="inline-flex items-center gap-1 text-xs text-foreground-500 whitespace-nowrap">
                        <i className="ri-eye-line"></i>
                        {c.votes} 人看过
                        <span className="ml-1 inline-flex items-center gap-0.5 text-secondary-600">
                          <i className="ri-heart-fill text-accent-400"></i>
                          好评 {c.heat}%
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleSaved(c.id)}
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                          isSaved
                            ? "bg-accent-100 text-accent-600"
                            : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                        }`}
                      >
                        <i className={`${isSaved ? "ri-bookmark-fill" : "ri-bookmark-line"}`}></i>
                        {isSaved ? "已收藏" : "收藏"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}